import {
  capitalizeFirst,
  convertSubdivisionCodeToSimplifiedSubdivisionCode,
  formatText,
  formatWeightLb,
  getCommdityNameForCondensedShipmentShipment,
  getEquipmentTypesDisplay,
  getServiceTypeDisplayLabel,
  ISOStringToReadableNotLocalized,
} from "utils";

export interface ShipmentInfoLine {
  label: string;
  value: string;
}

export const SHIPMENT_LINK_LABEL =
  "View more details, and submit your quote here:";
export const SHIPMENT_INFO_SIGN_OFF = "Thanks,";

// Opens the message, in the shipper's voice. A project is several loads.
export function getShipmentInfoIntro(shipment: CondensedShipment): string {
  return shipment.info.type === "MULTI_SHIPMENT_PROJECT"
    ? "I have these loads available. If you can move these for me, please click the link below to send me your quote."
    : "I have this load available. If you can move this for me, please click the link below to send me your quote.";
}

function toCityProvince(point: CondensedShipmentPoint): string {
  const { city, province } = point.point_location;
  return province
    ? `${city}, ${convertSubdivisionCodeToSimplifiedSubdivisionCode(province)}`
    : city;
}

/*
  What a shipper pastes into an email or text to carriers. It leaves out the
  shipper's own reference (custom name): only the booked carrier sees that.
*/
export function getShipmentInfoLines(
  shipment: CondensedShipment,
): ShipmentInfoLine[] {
  const commodity = getCommdityNameForCondensedShipmentShipment(shipment.info);
  const lines: ShipmentInfoLine[] = [
    { label: "ID", value: String(shipment.info.id) },
    {
      label: "Route",
      value: `${toCityProvince(shipment.first_point)} to ${toCityProvince(shipment.last_point)}`,
    },
  ];
  // Same numbers and wording carriers see on the project: loads left of total.
  if (shipment.info.type === "MULTI_SHIPMENT_PROJECT" && shipment.project) {
    const { loads_available: total, loads_booked: booked } = shipment.project;
    lines.push({
      label: "Shipments Available",
      value: `${total - booked} of ${total}`,
    });
  }
  lines.push(
    {
      label: "Service",
      value: getServiceTypeDisplayLabel(shipment.info.service),
    },
    {
      label: "Ready Date",
      value: ISOStringToReadableNotLocalized(shipment.first_point.start_time),
    },
  );
  if (shipment.equipments.length > 0) {
    lines.push({
      label: "Equipment",
      value: getEquipmentTypesDisplay(shipment.equipments.map((eq) => eq.name)),
    });
  }
  lines.push({
    label: "Commodity",
    value: commodity
      ? capitalizeFirst(formatText(commodity))
      : "General Freight",
  });
  if (shipment.info.weight_lb != null) {
    lines.push({
      label: "Weight",
      value: formatWeightLb(shipment.info.weight_lb, { includeKg: false }),
    });
  }
  return lines;
}

// The normal shipment page, which carriers on Evotrux open to quote.
export function getShipmentLink(shipment: CondensedShipment): string {
  return `${window.location.origin}/shipments/${shipment.info.id}`;
}

export function buildShipmentInfoText(shipment: CondensedShipment): string {
  return [
    getShipmentInfoIntro(shipment),
    "",
    ...getShipmentInfoLines(shipment).map(
      ({ label, value }) => `${label}: ${value}`,
    ),
    "",
    `${SHIPMENT_LINK_LABEL} ${getShipmentLink(shipment)}`,
    "",
    SHIPMENT_INFO_SIGN_OFF,
  ].join("\n");
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

const BLANK_LINE = "<div><br></div>";

/*
  Same content with the line titles bold, for pasting into email. One <div>
  per line, the way email editors write their own lines: loose text joined
  with <br>s pastes into Gmail with a stray space in front of the first line.
*/
export function buildShipmentInfoHtml(shipment: CondensedShipment): string {
  const link = escapeHtml(getShipmentLink(shipment));
  return [
    `<div>${escapeHtml(getShipmentInfoIntro(shipment))}</div>`,
    BLANK_LINE,
    ...getShipmentInfoLines(shipment).map(
      ({ label, value }) =>
        `<div><b>${escapeHtml(label)}:</b> ${escapeHtml(value)}</div>`,
    ),
    BLANK_LINE,
    `<div>${escapeHtml(SHIPMENT_LINK_LABEL)} <a href="${link}">${link}</a></div>`,
    BLANK_LINE,
    `<div>${escapeHtml(SHIPMENT_INFO_SIGN_OFF)}</div>`,
  ].join("");
}

/*
  Puts both versions on the clipboard: email apps paste the bold one, plain
  text boxes (texts, some chat apps) paste the plain one. Browsers without
  ClipboardItem get the plain text only.
*/
export async function copyShipmentInfo(
  shipment: CondensedShipment,
): Promise<void> {
  const text = buildShipmentInfoText(shipment);
  if (typeof ClipboardItem === "undefined") {
    await navigator.clipboard.writeText(text);
    return;
  }
  await navigator.clipboard.write([
    new ClipboardItem({
      "text/plain": new Blob([text], { type: "text/plain" }),
      "text/html": new Blob([buildShipmentInfoHtml(shipment)], {
        type: "text/html",
      }),
    }),
  ]);
}
